import { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../lib/api/apiClient';

export interface StoreQueryOptions {
  leagueId?: number;
  teamId?: number;
  activeOnly?: boolean;
}

// Normalized item shape surfaced to the UI. The backend may return a
// "listing" with a linked item payload; we extract common fields here.
export interface StoreItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency?: string;
  category?: string;
  // Raw backend payload for advanced use if needed
  raw?: unknown;
}

interface UseShopInventoryResult {
  items: StoreItem[];
  salaryCapItems: StoreItem[];
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

function buildQueryParams(options?: StoreQueryOptions) {
  const params: Record<string, string | number | boolean> = {};
  if (options?.leagueId != null) params.league_id = options.leagueId;
  if (options?.teamId != null) params.team_id = options.teamId;
  if (options?.activeOnly != null) params.active_only = options.activeOnly;
  return params;
}

function normalizeItems(data: any): StoreItem[] {
  // Accept a variety of backend shapes safely
  const list: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : [];

  return list.map((entry) => {
    // Expected structure (listing + linked item):
    // {
    //   id, item_id, price_cents, currency, is_active, league_id, team_id, metadata,
    //   item: { id, key, name_en, description_en, ... }
    // }
    const linked = entry?.item || {};

    const id = String(entry?.id ?? entry?.item_id ?? linked?.id ?? cryptoRandomId());
    const name = String(
      linked?.name_en ?? linked?.name ?? entry?.name ?? 'Untitled'
    );
    const description =
      linked?.description_en ?? linked?.description ?? entry?.description ?? undefined;

    // Convert cents to dollars if price_cents is present
    const priceCents = Number(entry?.price_cents);
    const priceFromLinked = Number(linked?.price);
    const priceNum = Number.isFinite(priceCents)
      ? priceCents / 100
      : Number.isFinite(priceFromLinked)
      ? priceFromLinked
      : Number(entry?.price ?? 0);

    const currency = entry?.currency ?? linked?.currency ?? 'USD';
    const category = linked?.key ?? linked?.category ?? entry?.category ?? undefined;

    return {
      id,
      name,
      description,
      price: Number.isFinite(priceNum) ? priceNum : 0,
      currency,
      category,
      raw: entry,
    } as StoreItem;
  });
}

// Fallback ID when backend doesn't provide one
function cryptoRandomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
      const buf = new Uint32Array(2);
      (crypto as Crypto).getRandomValues(buf);
      return `${buf[0].toString(16)}${buf[1].toString(16)}`;
    }
  } catch {
    // ignore
  }
  return Math.random().toString(36).slice(2);
}

export function useShopInventory(options?: StoreQueryOptions): UseShopInventoryResult {
  const [items, setItems] = useState<StoreItem[]>([]);
  const [salaryCapItems, setSalaryCapItems] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  // Stabilize options dependency
  const stableOptions = useMemo(() => ({ ...options }), [options?.leagueId, options?.teamId, options?.activeOnly]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildQueryParams(stableOptions);
      const response = await apiClient.get('/store', { params });
      const normalized = normalizeItems(response?.data);
      
      // Separate salary cap items from regular items
      const salaryCap: StoreItem[] = [];
      const regular: StoreItem[] = [];
      
      normalized.forEach((item) => {
        if (item.name && item.name.includes('Salary Cap')) {
          const renamedItem = item.name.replace('Salary Cap:', 'Increase to ');
          salaryCap.push({ ...item, name: renamedItem });
        } else {
          regular.push(item);
        }
      });
      
      setItems(regular);
      setSalaryCapItems(salaryCap);
    } catch (err) {
      setError(err);
      setItems([]);
      setSalaryCapItems([]);
    } finally {
      setLoading(false);
    }
  }, [stableOptions]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, salaryCapItems, loading, error, refetch: fetchItems };
}

export default useShopInventory;


